/**
 * Tenant Utilities
 * Centralized functions for getting tenant ID from multiple sources
 */

import { getApiBaseUrl } from '../lib/apiBaseUrl'

type SharedDomainMapping = {
  tenantId: string
  type?: 'admin' | 'user' | 'api'
  name?: string
  tier?: string
  verified?: boolean
  isPlatform?: boolean
}

type SharedDomainMappingsOptions = {
  cacheKey: string
  urls: string[]
  ttlMs?: number
  debug?: boolean
}

type SharedDomainMappingsCacheEntry = {
  timestamp: number
  data: Record<string, SharedDomainMapping>
}

const sharedDomainMappingsCache = new Map<string, SharedDomainMappingsCacheEntry>()
const getSharedDomainMappingsStorageKey = (cacheKey: string): string => `domainMappings:${cacheKey}`

const normalizeDomainHost = (host: string): string => host.trim().toLowerCase()

const readStoredDomainMappings = (cacheKey: string): SharedDomainMappingsCacheEntry | null => {
  try {
    const raw = localStorage.getItem(getSharedDomainMappingsStorageKey(cacheKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SharedDomainMappingsCacheEntry
    if (!parsed || typeof parsed.timestamp !== 'number' || !parsed.data || typeof parsed.data !== 'object') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const writeStoredDomainMappings = (cacheKey: string, entry: SharedDomainMappingsCacheEntry): void => {
  try {
    localStorage.setItem(getSharedDomainMappingsStorageKey(cacheKey), JSON.stringify(entry))
  } catch {
    // Ignore storage quota/private mode errors.
  }
}

const getSharedDomainMappings = async (
  options: SharedDomainMappingsOptions
): Promise<Record<string, SharedDomainMapping>> => {
  const ttlMs = options.ttlMs ?? 5 * 60 * 1000
  const cacheEntry = sharedDomainMappingsCache.get(options.cacheKey)
  const now = Date.now()

  if (cacheEntry && now - cacheEntry.timestamp < ttlMs) {
    if (options.debug) console.log('📦 Using cached domain mappings')
    return cacheEntry.data
  }

  const storedEntry = readStoredDomainMappings(options.cacheKey)
  if (storedEntry && now - storedEntry.timestamp < ttlMs) {
    sharedDomainMappingsCache.set(options.cacheKey, storedEntry)
    if (options.debug) console.log('💾 Using stored domain mappings')
    return storedEntry.data
  }

  for (const url of options.urls) {
    if (!url) continue

    try {
      const response = await fetch(url)
      if (!response.ok) {
        if (options.debug) console.warn(`⚠️ domain-mappings ${url} -> ${response.status}`)
        continue
      }

      const data = await response.json()
      if (!data?.success) continue

      const rawMappings = data?.data?.mappings || {}
      const mappings: Record<string, SharedDomainMapping> = {}

      Object.entries(rawMappings).forEach(([domain, mapping]) => {
        const value = mapping as SharedDomainMapping
        mappings[normalizeDomainHost(domain)] = {
          ...value,
          verified: value.verified ?? true,
        }
      })

      const nextEntry = { timestamp: now, data: mappings }
      sharedDomainMappingsCache.set(options.cacheKey, nextEntry)
      writeStoredDomainMappings(options.cacheKey, nextEntry)
      return mappings
    } catch (error) {
      if (options.debug) console.warn(`⚠️ domain-mappings fetch error (${url}):`, error)
    }
  }

  return {}
}

const resolveDomainMappingForHost = (
  mappings: Record<string, SharedDomainMapping>,
  host: string
): SharedDomainMapping | null => {
  const normalizedHost = normalizeDomainHost(host)
  const hostnameOnly = normalizeDomainHost(normalizedHost.split(':')[0] || normalizedHost)
  const bareHost = hostnameOnly.replace(/^www\./, '')
  const candidates = Array.from(new Set([normalizedHost, hostnameOnly, bareHost]))

  for (const candidate of candidates) {
    if (candidate && mappings[candidate]) {
      return mappings[candidate]
    }
  }

  return null
}

const isLocalDevelopmentHost = (hostname: string): boolean => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.0.0.') ||
    hostname.includes('localhost')
  )
}

const DEFAULT_DEV_TENANT_ID = 'test-demo-01'

const getHostname = (): string => window.location.hostname

const getHostScopedTenantKey = (hostname: string): string => `tenantId:${hostname}`

const getUrlTenantId = (): string | null => {
  return new URLSearchParams(window.location.search).get('tenant')
}

const getCurrentRoutePath = (): string => {
  if (typeof window === 'undefined') {
    return '/'
  }

  const hash = window.location.hash || ''
  if (hash.startsWith('#/')) {
    const routePath = hash.slice(1).split('?')[0]?.toLowerCase() || '/'
    return routePath || '/'
  }

  return window.location.pathname.toLowerCase() || '/'
}

const isPublicTenantRoute = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  const pathname = getCurrentRoutePath()
  return (
    pathname === '/' ||
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/register' ||
    pathname.startsWith('/register/')
  )
}

const getEnvTenantId = (): string | null => {
  return (import.meta.env as any).VITE_TENANT_ID || null
}

const getFallbackTenantId = (): string | null => {
  return (import.meta.env as any).VITE_FALLBACK_TENANT_ID || null
}

export const cacheResolvedTenantId = (tenantId: string, hostname: string = getHostname()): void => {
  if (!tenantId) return

  localStorage.setItem('tenantId', tenantId)
  localStorage.setItem(getHostScopedTenantKey(hostname), tenantId)

  const bareHostname = hostname.replace(/^www\./, '')
  if (bareHostname !== hostname) {
    localStorage.setItem(getHostScopedTenantKey(bareHostname), tenantId)
  }
}

export const getOptionalTenantId = (user?: { tenantId?: string } | null): string | null => {
  const hostname = getHostname()
  const localDev = isLocalDevelopmentHost(hostname)

  const hostScopedTenantId = localStorage.getItem(getHostScopedTenantKey(hostname))
  if (hostScopedTenantId) {
    return hostScopedTenantId
  }

  if (localDev) {
    const envTenantId = getEnvTenantId()
    if (envTenantId) {
      return envTenantId
    }
  }

  const urlTenantId = getUrlTenantId()
  if (urlTenantId) {
    return urlTenantId
  }

  if (user?.tenantId && !(isPublicTenantRoute() && !localDev)) {
    cacheResolvedTenantId(user.tenantId, hostname)
    return user.tenantId
  }

  if (!localDev && isPublicTenantRoute()) {
    return null
  }

  // Keep generic tenantId only for local development to avoid cross-domain cache pollution
  // such as test1 -> test2 reusing the wrong tenant across different public hostnames.
  if (localDev) {
    const storedTenantId = localStorage.getItem('tenantId')
    if (storedTenantId) {
      return storedTenantId
    }
  }

  return null
}

export const resolveTenantIdFromDomainAPI = async (hostname: string = getHostname()): Promise<string | null> => {
  const apiBase = getApiBaseUrl()

  try {
    const mappings = await getSharedDomainMappings({
      cacheKey: 'user-classic-public',
      urls: [apiBase ? `${apiBase}/api/v1/tenant-management/domain-mappings` : '/api/v1/tenant-management/domain-mappings'],
      debug: false,
    })
    const resolvedTenantId = resolveDomainMappingForHost(mappings, hostname)?.tenantId || null

    if (resolvedTenantId) {
      cacheResolvedTenantId(resolvedTenantId, hostname)
      return resolvedTenantId
    }
  } catch (error) {
    console.warn('⚠️ Failed to resolve tenant ID from domain mapping:', error)
  }

  return null
}

export const resolveTenantId = async (user?: { tenantId?: string } | null): Promise<string> => {
  const hostname = getHostname()
  const localDev = isLocalDevelopmentHost(hostname)
  const optionalTenantId = getOptionalTenantId(user)

  if (!localDev && optionalTenantId) {
    // Host-scoped cache is safe to use immediately; revalidate the authoritative mapping in background.
    void resolveTenantIdFromDomainAPI(hostname).catch((error) => {
      console.warn('⚠️ Background tenant mapping refresh failed:', error)
    })
    return optionalTenantId
  }

  if (!localDev) {
    // Public hosts must trust the authoritative domain mapping before any cached or inferred tenant.
    const mappedTenantId = await resolveTenantIdFromDomainAPI(hostname)
    if (mappedTenantId) {
      return mappedTenantId
    }
  }

  if (optionalTenantId) {
    return optionalTenantId
  }

  if (localDev) {
    const fallbackTenantId = getFallbackTenantId()
    if (fallbackTenantId) {
      cacheResolvedTenantId(fallbackTenantId, hostname)
      return fallbackTenantId
    }
  }

  if (import.meta.env.PROD) {
    console.error('❌ No tenant ID found! Please configure domain-mappings or tenant environment variables.')
    throw new Error('Tenant ID is required but not found. Please configure domain-mappings or tenant environment variables.')
  }

  console.warn('⚠️ No tenant ID found, using development fallback.')
  cacheResolvedTenantId(DEFAULT_DEV_TENANT_ID, hostname)
  return DEFAULT_DEV_TENANT_ID
}

/**
 * Get tenant ID from multiple sources (priority order)
 * 1. From authenticated user (user?.tenantId)
 * 2. From host-scoped cache/localStorage
 * 3. From environment variable (VITE_TENANT_ID)
 * 4. From URL parameter (?tenant=xxx)
 * 5. From fallback environment variable
 * 6. Development fallback / throw in production
 */
export const getTenantId = (user?: { tenantId?: string } | null): string => {
  const hostname = getHostname()
  const tenantId = getOptionalTenantId(user)
  if (tenantId) {
    return tenantId
  }

  if (isLocalDevelopmentHost(hostname)) {
    const fallbackTenantId = getFallbackTenantId()
    if (fallbackTenantId) {
      return fallbackTenantId
    }
  }

  if (import.meta.env.PROD) {
    console.error('❌ No tenant ID found! Please configure VITE_TENANT_ID, VITE_FALLBACK_TENANT_ID, or domain mappings.')
    throw new Error('Tenant ID is required but not found. Please configure VITE_TENANT_ID, VITE_FALLBACK_TENANT_ID, or domain mappings.')
  }

  console.warn('⚠️ No tenant ID found, using development fallback.')
  return DEFAULT_DEV_TENANT_ID
}

/**
 * Get tenant ID from themeConfig (used in components that receive themeConfig as prop)
 * 
 * @param themeConfig - Optional theme config object
 * @param user - Optional user object from AuthContext
 * @returns Tenant ID string
 */
export const getTenantIdFromConfig = (
  themeConfig?: { tenantId?: string } | null,
  user?: { tenantId?: string } | null
): string => {
  // 1. From themeConfig (set by App.tsx)
  if (themeConfig?.tenantId) {
    return themeConfig.tenantId
  }
  
  // 2. Fallback to standard getTenantId
  return getTenantId(user)
}
